// src/components/Testimonials.js

import React, { useState, useEffect, useRef } from 'react';

const Testimonials = () => {
    // --- STATE MANAGEMENT ---
    const [testimonials, setTestimonials] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [modalContent, setModalContent] = useState(null);

    // NEW: State for swipe/drag functionality
    const [touchStartX, setTouchStartX] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    // NEW: Ref to hold the interval ID for auto-sliding
    const intervalRef = useRef(null);

    const fallbackTestimonials = [
        { id: 1, name: "Priya S.", designation: "TGT Science, Jaipur", message: "I was tired of applying to multiple schools without hearing back. Acadfinity matched me with a verified RBSE school in Jaipur within a week. The process was clear, genuine, and completely free. It's a fantastic platform for any teacher looking for a new role in Rajasthan.", image_path: "https://randomuser.me/api/portraits/women/44.jpg" },
        { id: 2, name: "R. K. Verma", designation: "Vice Principal, Jodhpur", message: "As a senior educator, confidentiality was key. Their team understood my requirements perfectly and connected me with a prestigious institution that valued my experience. The transition was smooth and professional.", image_path: "https://randomuser.me/api/portraits/men/32.jpg" },
        { id: 3, name: "Anjali Mehta", designation: "PRT English, Kota", message: "Acadfinity is the best platform for teachers. I got a call within 3 days and was hired within a week. Highly recommended for anyone serious about their teaching career!", image_path: "https://randomuser.me/api/portraits/women/55.jpg" }
    ];

    // --- HELPER FUNCTIONS FOR NAVIGATION ---
    const goToNext = () => {
        setCurrentIndex(prev => (prev + 1) % testimonials.length);
    };

    const goToPrev = () => {
        setCurrentIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
    };

    // --- AUTO-SLIDE LOGIC ---
    const startAutoSlide = () => {
        intervalRef.current = setInterval(goToNext, 4000);
    };

    const resetAutoSlide = () => {
        clearInterval(intervalRef.current);
        startAutoSlide();
    };

    useEffect(() => {
        fetch('/api/testimonials')
            .then(res => res.json())
            .then(data => setTestimonials(data && data.length > 0 ? data : fallbackTestimonials))
            .catch(() => setTestimonials(fallbackTestimonials));
    }, []);
    
    // Start and manage the auto-slide interval
    useEffect(() => {
        if (testimonials.length > 0) {
            startAutoSlide();
        }
        return () => clearInterval(intervalRef.current); // Cleanup on component unmount
    }, [testimonials.length]); // Re-run only if the number of testimonials changes

    // Prevent body scroll when modal is open
    useEffect(() => {
        document.body.style.overflow = modalContent ? 'hidden' : 'auto';
    }, [modalContent]);

    // --- SWIPE/DRAG HANDLERS ---
    const handleInteractionStart = (clientX) => {
        setIsDragging(true);
        setTouchStartX(clientX);
        clearInterval(intervalRef.current); // Pause auto-slide on interaction
    };

    const handleInteractionEnd = (clientX) => {
        if (!isDragging || touchStartX === null) return;

        const touchEndX = clientX;
        const swipeDistance = touchStartX - touchEndX;
        const minSwipeDistance = 50; // Minimum distance for a swipe to register

        if (swipeDistance > minSwipeDistance) {
            goToNext();
        } else if (swipeDistance < -minSwipeDistance) {
            goToPrev();
        }
        
        resetAutoSlide(); // Resume auto-slide after interaction
        setIsDragging(false);
        setTouchStartX(null);
    };

    // For Touch Devices
    const handleTouchStart = (e) => handleInteractionStart(e.targetTouches[0].clientX);
    const handleTouchEnd = (e) => handleInteractionEnd(e.changedTouches[0].clientX);

    // For Mouse Devices
    const handleMouseDown = (e) => handleInteractionStart(e.clientX);
    const handleMouseUp = (e) => handleInteractionEnd(e.clientX);
    const handleMouseLeave = () => { // If mouse leaves the area while dragging
        if (isDragging) {
            resetAutoSlide();
            setIsDragging(false);
            setTouchStartX(null);
        }
    };


    // This component renders a single testimonial card
    const TestimonialCard = ({ testimonial }) => {
        if (!testimonial) return null;
        const isLongText = testimonial.message.length > 150;

        return (
            <div className="bg-slate-800 p-8 rounded-3xl flex flex-col h-[380px] w-full max-w-lg mx-auto">
                <svg className="w-8 h-8 text-orange-500 mb-4 shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M10.811 2.652c-2.43.243-4.226 2.348-4.01 4.79.215 2.443 2.32 4.24 4.76 4.025a4.5 4.5 0 004.025-4.762 4.5 4.5 0 00-4.775-4.053zm8.388.017c-2.43.243-4.226 2.348-4.01 4.79.215 2.443 2.32 4.24 4.76 4.025a4.5 4.5 0 004.025-4.762 4.5 4.5 0 00-4.775-4.053z"/></svg>
                <div className="flex-1 overflow-hidden">
                    <p className="text-slate-300 italic line-clamp-4">"{testimonial.message}"</p>
                </div>
                {isLongText && (
                    <button onClick={() => setModalContent(testimonial)} className="text-orange-400 text-sm font-bold self-start mt-2 hover:underline">
                        Read More
                    </button>
                )}
                <div className="mt-auto pt-4 border-t border-slate-700/50">
                    <div className="flex items-center gap-4">
                        <img className="w-12 h-12 rounded-full object-cover" src={testimonial.image_path} onError={(e) => {e.target.src = 'https://via.placeholder.com/150'}} alt={testimonial.name} />
                        <div>
                            <div className="font-bold text-white">{testimonial.name}</div>
                            <div className="text-xs text-slate-400">{testimonial.designation}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <section id="testimonials" className="py-20 bg-slate-900">
            <div className="max-w-6xl mx-auto px-4 md:px-8">
                <h2 className="text-3xl font-black text-white uppercase tracking-tight text-center">Success Stories</h2>
                <p className="text-slate-400 mt-2 mb-12 text-center">
                    Real educators. Real schools. Transparent hiring through Acadfinity.
                </p>
                
                {/* --- UPDATED SLIDER CONTAINER --- */}
                <div 
                    className="relative overflow-hidden cursor-grab active:cursor-grabbing"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseLeave}
                >
                    {/* Render only the current testimonial */}
                    <div key={currentIndex} className="animate-in fade-in duration-500">
                        <TestimonialCard testimonial={testimonials[currentIndex]} />
                    </div>

                    {/* Navigation Dots */}
                    <div className="flex justify-center mt-8 gap-2">
                        {testimonials.map((_, index) => (
                            <button 
                                key={index} 
                                onClick={() => {
                                    setCurrentIndex(index);
                                    resetAutoSlide(); // Also reset timer on dot click
                                }} 
                                className={`w-2 h-2 rounded-full transition-all ${index === currentIndex ? 'bg-orange-500 w-6' : 'bg-slate-700'}`}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* Modal Popup (No changes needed here) */}
            {modalContent && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[500] p-4 animate-in fade-in duration-300">
                    <div className="bg-slate-800 p-8 rounded-2xl max-w-lg w-full relative border border-slate-700">
                        <button onClick={() => setModalContent(null)} className="absolute top-3 right-3 text-slate-500 hover:text-white transition-colors">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                        <div className="flex items-center gap-4 mb-6">
                            <img className="w-16 h-16 rounded-full object-cover" src={modalContent.image_path} onError={(e) => {e.target.src = 'https://via.placeholder.com/150'}} alt={modalContent.name} />
                            <div>
                                <div className="font-bold text-lg text-white">{modalContent.name}</div>
                                <div className="text-sm text-slate-400">{modalContent.designation}</div>
                            </div>
                        </div>
                        <p className="text-slate-300 italic leading-relaxed">"{modalContent.message}"</p>
                    </div>
                </div>
            )}
        </section>
    );
};

export default Testimonials;